import { Accordion } from "@/components/ui/accordion";
import { SupportedLocale } from "@/i18n";
import { Artwork, SculptureEdition } from "@/lib/types";
import {
  ComputedFields,
  MergedDisplayData,
  RelatedFieldsWithAttachments,
} from "../types";
import { AuctionsAccordion } from "./AuctionsAccordion";
import { BibliographyAccordion } from "./BibliographyAccordion";
import { CreditsAccordion } from "./CreditsAccordion";
import { ExhibitionsAccordion } from "./ExhibitionsAccordion";
import { ExternalLinksAccordion } from "./ExternalLinksAccordion";
import { HistoryAccordion } from "./HistoryAccordion";
import { RelatedWorksAccordion } from "./RelatedWorksAccordion";
import { SculptureEditionsAccordion } from "./SculptureEditionsAccordion";
import { TirageKnownDescriptionAccordion } from "./TirageKnownDescriptionAccordion";
import { TiragesAccordion } from "./TiragesAccordion";

interface ArtworkAccordionsProps {
  displayData: MergedDisplayData;
  data: Artwork;
  computedFields: ComputedFields;
  relatedFieldsWithAttachments: RelatedFieldsWithAttachments;
  relationNotEmpty: boolean;
  isTirage: boolean;
  isPhotoMaster: boolean;
  isPhotoModuleEnabled: boolean;
  isSculptureEdition: boolean;
  isSculptureMaster: boolean;
  sculptureEditions: SculptureEdition[];
  locale: SupportedLocale;
  t: (key: string, options?: any) => string;
  provenanceMode?: boolean;
}

export {
  ACCORDION_LIST_CONTENT_CLASS,
  AccordionClass,
} from "@/lib/accordion-content";

export function ArtworkAccordions({
  displayData,
  data,
  computedFields,
  relatedFieldsWithAttachments,
  relationNotEmpty,
  isTirage,
  isPhotoMaster,
  isPhotoModuleEnabled,
  isSculptureEdition,
  isSculptureMaster,
  sculptureEditions,
  locale,
  t,
  provenanceMode = false,
}: ArtworkAccordionsProps) {
  const hasHistory =
    !!computedFields.historyCalculated ||
    !!computedFields.tirageHistoryCalculated ||
    !!computedFields.sculptureEditionHistoryCalculated ||
    (displayData.artworkHistory?.length ?? 0) > 0 ||
    (displayData.tirageHistory?.length ?? 0) > 0 ||
    (displayData.sculptureEditionHistory?.length ?? 0) > 0;

  const historyAccordionTitle = provenanceMode
    ? t("artwork.accordion.provenance.title")
    : t("artwork.accordion.history.title");

  // When viewing a Tirage, ensure we check for tirage-specific relations
  // The mergeDisplayData function normalizes tirage relations to artwork format,
  // but we also check raw tirage data as a fallback to ensure completeness
  const tirageData = isTirage ? (data as any)?.tirageData : null;

  // Check for exhibitions: displayData should have normalized tirage relations,
  // but we also check raw tirage data when viewing a Tirage
  const hasExhibitions =
    (displayData.artworkInExhibitions?.length ?? 0) > 0 ||
    (isTirage && (tirageData?.tirageInExhibition?.length ?? 0) > 0);

  // Check for bibliography
  const hasBibliography =
    (displayData.artworkInBibliography?.length ?? 0) > 0 ||
    (isTirage && (tirageData?.tirageInBibliography?.length ?? 0) > 0);

  // Check for auctions
  const hasAuctions =
    (displayData.artworkInAuctions?.length ?? 0) > 0 ||
    (isTirage && (tirageData?.tirageInAuction?.length ?? 0) > 0);

  return (
    <Accordion
      type="multiple"
      className="font-normal text-base text-gray-800 mb-32"
    >
      {hasHistory && (
        <HistoryAccordion
          historyCalculated={computedFields.historyCalculated}
          tirageHistoryCalculated={
            isSculptureEdition
              ? (computedFields.sculptureEditionHistoryCalculated ?? null)
              : computedFields.tirageHistoryCalculated
          }
          t={t}
          title={historyAccordionTitle}
        />
      )}
      {hasExhibitions && computedFields.exhibitionsCalculated && (
        <ExhibitionsAccordion
          exhibitionsCalculated={computedFields.exhibitionsCalculated}
          t={t}
        />
      )}
      {hasBibliography && computedFields.bibliographyCalculated && (
        <BibliographyAccordion
          bibliographyCalculated={computedFields.bibliographyCalculated}
          t={t}
        />
      )}
      {!provenanceMode && hasAuctions && computedFields.auctionCalculated && (
        <AuctionsAccordion
          auctionCalculated={computedFields.auctionCalculated}
          t={t}
        />
      )}
      <CreditsAccordion
        displayData={displayData}
        isPhotoMaster={isPhotoMaster}
        isTirage={isTirage}
        locale={locale}
        t={t}
      />
      <TiragesAccordion
        displayData={displayData}
        isTirage={isTirage}
        isPhotoModuleEnabled={isPhotoModuleEnabled}
        locale={locale}
        t={t}
      />
      <SculptureEditionsAccordion
        displayData={displayData}
        sculptureEditions={sculptureEditions}
        isSculptureEdition={isSculptureEdition}
        isSculptureMaster={isSculptureMaster}
        locale={locale}
        t={t}
      />
      <TirageKnownDescriptionAccordion
        data={data}
        isPhotoMaster={isPhotoMaster}
        locale={locale}
        t={t}
      />
      <RelatedWorksAccordion
        relatedFieldsWithAttachments={relatedFieldsWithAttachments}
        relationNotEmpty={relationNotEmpty}
        locale={locale}
        t={t}
      />
      <ExternalLinksAccordion artworkId={data?.id} t={t} />
    </Accordion>
  );
}
